import clsx from 'clsx';
import React from 'react';
import Icon from '../../atoms/Icon/Icon';
import ProgressBar from '../../atoms/ProgressBar/ProgressBar';
import Spinner from '../../atoms/Spinner/Spinner';
import type { UploadProgressListProps } from './types';
import useUploadProgressList from './useUploadProgressList';

/**
 * UploadProgressList — v2 molecule.
 *
 * Lista accesible del progreso de una subida secuencial de archivos. Cada
 * archivo muestra su nombre y un afijo según su estado: una {@link ProgressBar}
 * mientras se sube (`uploading`) o un {@link Icon} de estado con texto oculto
 * (`pending` | `done` | `error`). Cuando `isProcessing` es `true` muestra un
 * bloque con {@link Spinner} y un label de procesamiento. Las transiciones se
 * anuncian a los lectores de pantalla vía regiones `aria-live`.
 */
function UploadProgressList({ className, files, isProcessing = false, processingLabel }: UploadProgressListProps): JSX.Element {
  const { announcement, getStatusIconColor, getStatusIconName, getStatusLabel, processingLabelText } = useUploadProgressList({
    files,
    processingLabel,
  });

  return (
    <div className={clsx('upload-progress-list', className)}>
      <ul className="upload-progress-list__list">
        {files.map((file, index) => (
          <li key={`${file.name}-${index}`} className="upload-progress-list__item">
            <span className="upload-progress-list__name">{file.name}</span>
            <span className="upload-progress-list__affix">
              {file.status === 'uploading' ? (
                <ProgressBar isLabelHidden label={file.name} progress={file.progress} size="medium" />
              ) : (
                <>
                  <Icon color={getStatusIconColor(file.status)} name={getStatusIconName(file.status)} size="md" />
                  <span className="sr-only">{getStatusLabel(file.status)}</span>
                </>
              )}
            </span>
          </li>
        ))}
      </ul>

      {isProcessing && (
        <output className="upload-progress-list__processing">
          <Spinner />
          <span className="upload-progress-list__processing-label">{processingLabelText}</span>
        </output>
      )}

      <span aria-live="polite" className="sr-only">
        {announcement}
      </span>
    </div>
  );
}

export default UploadProgressList;
